import { useState, useEffect, useCallback } from 'react';
import { graphql } from '../../post';
import {
  getFormatedValue,
  getHousingLabel,
  deviceTypeLabel,
  getDeviceLabel,
} from '@utils/utils';

/**
 * Hook personnalisé pour la logique métier du composant Popup
 *
 * @param {Object} params - Paramètres du hook
 * @param {Object} params.me - Données utilisateur
 * @param {Object} params.context - Contexte de l'application
 * @param {Function} params.t - Fonction de traduction
 * @returns {Object} État et handlers du composant
 */
export const usePopupLogic = ({ me, context, t }) => {
  const [state, setState] = useState({
    popupLeakageNoShowAgain: false,
    popup: undefined,
  });

  // Fermeture de popup
  const closePopup = useCallback(() => {
    setState((prev) => ({ ...prev, popup: undefined }));
    return Promise.resolve();
  }, []);

  // Émission d'événements
  const emitEvent = useCallback((type, data, cb = () => {}) => {
    console.log('popup emitEvent', type, data, cb);

    switch (type) {
      case 'closePopup':
        closePopup();
        break;
      case 'confirm':
      case 'customPopup':
      case 'alert':
      case 'leakagePopup':
        setState((prev) => ({ ...prev, popup: { type, data } }));
        cb();
        break;
      default:
        cb();
    }
  }, [closePopup]);

  // Gestion des événements popup
  const handlePopupEvent = useCallback((event) => {
    const { type, data, cb } = event.detail;
    emitEvent(type, data, cb);
  }, [emitEvent]);

  // Affichage de la popup de fuite
  const showLeakagePopup = useCallback(() => {
    if (!me?.leakage_alarms || me.leakage_alarms.length === 0) {
      return;
    }

    const leakagePopupData = {
      title: t('layout.popupleakage.title'),
      text: t('layout.popupleakage.text', { count: me.leakage_alarms.length }),
      alarms: me.leakage_alarms.map((alarm) => ({
        id: alarm.id,
        jointHousingGroupLabel: alarm.Device?.Housing?.HousingGroup?.JointHousingGroup?.label,
        address: alarm.Device?.Housing?.HousingGroup?.address,
        housingLabel: getHousingLabel(alarm.Device?.Housing),
        deviceType: alarm.Device ? t(`global.deviceType.${deviceTypeLabel(alarm.Device?.type)}`) : '',
        deviceLabel: getDeviceLabel(alarm.Device)
      })),
      checkbox: {
        checked: state.popupLeakageNoShowAgain,
        onChange: (e) => {
          setState((prev) => ({
            ...prev,
            popupLeakageNoShowAgain: e.target.checked,
          }));
        },
        label: t('layout.popupleakage.noshowagain', {
          count: me.leakage_alarms.length,
        })
      },
      actionButtons: [
        {
          label: 'OK',
          action: async () => {
            if (state.popupLeakageNoShowAgain) {
              const values = me.leakage_alarms.map((alarm) => {
                let value = '{';
                if (alarm.AlarmNotifs?.id !== undefined) {
                  value += `id: ${alarm.AlarmNotifs?.id}, `;
                } else {
                  value += `date: ${getFormatedValue(new Date().toISOString())}, `;
                  value += `user_id: ${me.id}, `;
                  value += `alarm_id: ${alarm.id}, `;
                }
                value += 'status: 50';
                value += '}';
                return value;
              });

              try {
                await graphql(
                  context.fetch,
                  `mutation {setAlarmNotif(objects: [${values.join(', ')}]){id, date, user_id, alarm_id, status}}`,
                );
              } catch (err) {
                console.error('Error setting alarm notification:', err);
              }
            }else{
              // On mémorise que l'utilisateur a déjà vu la popup de fuite
              // On n'affiche plus la popup de fuite tant que la page n'est pas rechargée
              window.popupLeakageNoShowAgain = true;
            }
            closePopup();
          },
        },
      ],
    };

    setState((prev) => ({
      ...prev,
      popup: {
        type: 'leakagePopup',
        data: leakagePopupData,
      },
    }));
  }, [me, context, t, state.popupLeakageNoShowAgain, closePopup]);

  // Affichage de la popup de consentement de contact (uniquement type >= 40 et contactConsent non défini)
  const showContactConsentPopup = useCallback(() => {
    const saveConsent = async (value) => {
      try {
        await graphql(
          context.fetch,
          `mutation{setProfile(contactConsent: ${value}){contactConsent}}`,
        );
        if (me) {
          me.contactConsent = value;
        }
      } catch (err) {
        console.error('Error saving contact consent:', err);
      }
    };

    setState((prev) => ({
      ...prev,
      popup: {
        type: 'confirm',
        data: {
          text: t('layout.popupcontactconsent.text'),
          cancelButtonText: t('layout.popupcontactconsent.refuse'),
          validateButtonText: t('layout.popupcontactconsent.accept'),
          handleValidate: () => saveConsent(1),
          handleCancel: () => saveConsent(0),
        },
      },
    }));
  }, [me, context, t]);

  // Effet pour écouter les événements popup
  useEffect(() => {
    window.addEventListener('popupEvent', handlePopupEvent);
    return () => {
      window.removeEventListener('popupEvent', handlePopupEvent);
    };
  }, [handlePopupEvent]);

  // Effet pour afficher la popup de fuite au montage
  useEffect(() => {
    if (me?.leakage_alarms && me.leakage_alarms.length > 0 && !window.popupLeakageNoShowAgain) {
      showLeakagePopup();
    }
  }, [me?.leakage_alarms, showLeakagePopup]);

  // Effet pour afficher la popup de consentement au montage (utilisateurs type >= 40 sans réponse)
  useEffect(() => {
    if (
      me?.type >= 40
      && (me.contactConsent === null || me.contactConsent === undefined)
      && !window.popupContactConsentShown
      && !(me?.leakage_alarms && me.leakage_alarms.length > 0 && !window.popupLeakageNoShowAgain)
    ) {
      window.popupContactConsentShown = true;
      showContactConsentPopup();
    }
  }, [me?.type, me?.contactConsent, showContactConsentPopup]);

  return {
    state,
    handlers: {
      emitEvent,
      closePopup,
      showLeakagePopup,
      showContactConsentPopup,
    },
  };
};

/**
 * Fonction utilitaire pour valider les données de popup
 *
 * @param {Object} data - Données à valider
 * @returns {Object} Résultat de la validation
 */
export const validatePopupData = (data) => {
  if (!data || typeof data !== 'object') {
    return {
      isValid: false,
      error: 'Popup data must be an object',
    };
  }

  if (!data.text && !data.children) {
    return {
      isValid: false,
      error: 'Popup must have either text or children',
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

/**
 * Fonction utilitaire pour formater les alarmes de fuite
 *
 * @param {Array} alarms - Liste des alarmes
 * @returns {Array} Alarmes formatées
 */
export const formatLeakageAlarms = (alarms) => {
  if (!Array.isArray(alarms)) {
    return [];
  }

  return alarms.map((alarm) => ({
    id: alarm.id,
    device: alarm.Device,
    housing: alarm.Device?.Housing,
    housingGroup: alarm.Device?.Housing?.HousingGroup,
    jointHousingGroup: alarm.Device?.Housing?.HousingGroup?.JointHousingGroup,
    alarmNotifs: alarm.AlarmNotifs,
  }));
};
